import {
  IonContent,
  IonDatetime,
  IonLoading,
  IonModal,
  IonPopover,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import OverviewTable from './overviewTable/OverviewTable';
import {
  DataItems,
  Empty,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { useDentist } from '@/contexts/DentistContext';
import { getAppointmentsByDateAndDentistIds } from '@/data/Requests';
import { useToast } from '@/hooks';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { useQuery } from '@tanstack/react-query';
import { format, startOfDay } from 'date-fns';
import {
  arrowBackOutline,
  calendarOutline,
  medkitOutline,
  peopleOutline
} from 'ionicons/icons';

import './OverviewModal.css';

interface OverviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const OverviewModal: React.FC<OverviewModalProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const { dentists } = useDentist();
  const displayToast = useToast();

  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(now()));
  const datePopoverRef = useRef<HTMLIonPopoverElement>(null);

  const dentistIds = useMemo(() => {
    return dentists.map((dentist) => dentist.id).filter(Boolean) as string[];
  }, [dentists]);

  const { data: appointmentsByDentist, isLoading } = useQuery({
    queryKey: ['appointments', selectedDate, dentistIds],
    queryFn: () => {
      try {
        return getAppointmentsByDateAndDentistIds(
          format(selectedDate, 'yyyy-MM-dd'),
          dentistIds
        );
      } catch (error) {
        displayToast({
          message: 'Failed to load appointments. Please try again.'
        });
        throw error;
      }
    },
    enabled: isOpen && dentistIds.length > 0
  });

  const overviewDataItems = useMemo(() => {
    if (!appointmentsByDentist) return [];

    const allAppointments = Object.values(appointmentsByDentist).flat();

    if (allAppointments.length === 0) return [];

    const totalAppointments = allAppointments.length;
    const averagePerDentist =
      dentists.length > 0
        ? (totalAppointments / dentists.length).toFixed(1)
        : '0';

    const procedureCounts: Record<string, number> = {};
    allAppointments.forEach((appointment) => {
      if (appointment.purpose) {
        procedureCounts[appointment.purpose] =
          (procedureCounts[appointment.purpose] || 0) + 1;
      }
    });
    const topProcedure =
      Object.entries(procedureCounts).sort(([, a], [, b]) => b - a)[0]?.[0] ||
      'None';

    return [
      {
        icon: calendarOutline,
        title: 'Total Appointments',
        value: totalAppointments
      },
      {
        icon: peopleOutline,
        title: 'Average per Dentist',
        value: averagePerDentist
      },
      {
        icon: medkitOutline,
        title: 'Top Procedure',
        value: topProcedure
      }
    ];
  }, [appointmentsByDentist, dentists]);

  const onSelectDate = useCallback(async (event: CustomEvent) => {
    setSelectedDate(startOfDay(parseDate(event.detail.value)));
    if (datePopoverRef.current) {
      await datePopoverRef.current.dismiss();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSelectedDate(startOfDay(now()));
    }

    return () => {
      setSelectedDate(startOfDay(now()));
    };
  }, [isOpen]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="overview-modal">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} />
            <div id="overview-modal__date" className="overview-modal__date">
              <IonSelect
                className="overview-modal__date-select"
                placeholder="Select Date"
                value={format(selectedDate, 'yyyy-MM-dd')}
                label="Date"
                color="dark"
                disabled={true}
              >
                <IonSelectOption value={format(selectedDate, 'yyyy-MM-dd')}>
                  {format(selectedDate, 'MMMM d, yyyy')}
                </IonSelectOption>
              </IonSelect>
            </div>
            <IonPopover
              ref={datePopoverRef}
              trigger="overview-modal__date"
              alignment="end"
              size="cover"
              arrow={false}
            >
              <IonDatetime
                presentation="date"
                onIonChange={onSelectDate}
                size="cover"
                value={format(selectedDate, 'yyyy-MM-dd')}
              />
            </IonPopover>
            <Spacer top={24} />
            {appointmentsByDentist && (
              <div>
                {Object.keys(appointmentsByDentist).length === 0 ||
                overviewDataItems.length === 0 ? (
                  <Empty
                    message={`No appointments on ${format(selectedDate, 'MMMM d, yyyy')}`}
                  />
                ) : (
                  <>
                    <OverviewTable
                      appointmentsByDentist={appointmentsByDentist}
                      onCloseModal={onClose}
                    />
                    <Spacer top={24} bottom={42}>
                      <DataItems items={overviewDataItems} />
                    </Spacer>
                  </>
                )}
              </div>
            )}
          </div>
        </Padding>

        <IonLoading
          isOpen={isLoading}
          message="Loading..."
          spinner="crescent"
        />
      </IonContent>
    </IonModal>
  );
};

export default OverviewModal;
